import ShipmentService from "./shipment"

const DAY = 24 * 60 * 60 * 1000
const CACHE_DURATION = 7 * DAY // 7 days
// Failed or incomplete loads are retried after minutes, not days (MAR-103).
const FAILURE_RETRY_DURATION = 5 * 60 * 1000 // 5 minutes
const REFRESH_THRESHOLD = 6 * DAY // 6 days - trigger refresh when near expiration
// One day below the refresh threshold, so the page refresh always reaches
// Daedalus instead of storing the same payload again.
const UPSTREAM_CACHE_DURATION = REFRESH_THRESHOLD - DAY
const CITY_DATA_CACHE: Record<string, TimedEntry<ShipmentData>> = {}
const STATE_DATA_CACHE: Record<string, TimedEntry<ShipmentData>> = {}
const TRANSPORTER_CACHE: Record<string, TimedEntry<TransporterRow[]>> = {}
const COOLDOWNS: {
  [key: string]: { timestamp: number; failed: boolean; data?: unknown }
} = {}
const RATE_LIMIT_DURATION = 60 * 1000 // 1 minute - shorter than the crawler's interval
// Concurrent misses for one key share a single load instead of each going upstream.
const IN_FLIGHT = new Map<string, Promise<unknown>>()

interface CacheEntry<T> {
  data: T
  isComplete: boolean
  expiresAt: number
  refreshAt: number
}

interface CacheOptions<T> {
  // A result that loaded but is missing data, e.g. the empty lists a page
  // falls back to when Daedalus fails. Retried soon instead of kept for 7 days.
  isIncomplete?: (data: T) => boolean
}

interface FetchResult<T> {
  data: T
  failed: boolean
}

interface TimedEntry<T> {
  data: T
  storedAt: number
}

type ShipmentLists = ShipmentData

const cacheStore: { [key: string]: CacheEntry<any> } = {}

const shareInFlight = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const pending = IN_FLIGHT.get(key)

  if (pending) {
    return pending as Promise<T>
  }

  const request = load().finally(() => IN_FLIGHT.delete(key))

  IN_FLIGHT.set(key, request)

  return request
}

const isEmptyResult = (data: unknown) =>
  !data || (typeof data === "object" && Object.keys(data).length === 0)

export const getCachedData = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
  { isIncomplete = () => false }: CacheOptions<T> = {},
): Promise<T> => {
  const cached = cacheStore[key] as CacheEntry<T> | undefined
  const now = Date.now()

  // A complete result remains available during an outage, even after its TTL.
  // Refresh it in the background so requests never wait on the failed upstream.
  if (cached && (cached.isComplete || now < cached.expiresAt)) {
    if (now >= cached.refreshAt) {
      void loadIntoCache(key, fetchFn, isIncomplete).catch((error) => {
        console.error(`Cache refresh failed for ${key}:`, error)
      })
    }

    return cached.data
  }

  return loadIntoCache(key, fetchFn, isIncomplete)
}

const loadIntoCache = <T>(
  key: string,
  fetchFn: () => Promise<T>,
  isIncomplete: (data: T) => boolean,
): Promise<T> =>
  shareInFlight(`cached:${key}`, async () => {
    let data: T | undefined

    try {
      data = await fetchFn()
    } catch (error) {
      console.error(`Cache load failed for ${key}:`, error)
    }

    const now = Date.now()
    let isComplete = false

    try {
      isComplete = !isEmptyResult(data) && !isIncomplete(data as T)
    } catch (error) {
      console.error(`Cache validation failed for ${key}:`, error)
    }

    if (!isComplete) {
      return storeFailedLoad(key, data, now)
    }

    cacheStore[key] = {
      data,
      isComplete: true,
      expiresAt: now + CACHE_DURATION,
      refreshAt: now + REFRESH_THRESHOLD,
    }

    return data as T
  })

// Serves the last complete result when there is one, otherwise whatever the
// failed load returned, and schedules the next upstream attempt a few minutes out.
const storeFailedLoad = <T>(
  key: string,
  data: T | undefined,
  now: number,
): T => {
  const previous = cacheStore[key] as CacheEntry<T> | undefined
  const retryAt = now + FAILURE_RETRY_DURATION

  if (previous?.isComplete) {
    cacheStore[key] = {
      ...previous,
      expiresAt: Math.max(previous.expiresAt, retryAt),
      refreshAt: retryAt,
    }

    return previous.data
  }

  const fallback = isEmptyResult(data) ? previous?.data : data
  const entryData = fallback ?? ({} as T)

  cacheStore[key] = {
    data: entryData,
    isComplete: false,
    expiresAt: retryAt,
    refreshAt: retryAt,
  }

  return entryData
}

// Add exponential backoff retry helper
export const withRetry = async <T>(
  fn: () => Promise<T>,
  retries = 3,
  baseDelay = 1000,
): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (error: any) {
      if (error?.response?.status === 429) {
        if (i === retries - 1) {
          throw error
        }

        const delay = baseDelay * Math.pow(2, retries - i - 1)

        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }

      throw error
    }
  }

  throw new Error("Max retries reached")
}

export const fetchWithRetry = withRetry

const hasCompleteShipments = (data: any) =>
  data?.exportShipments?.length > 0 && data?.importShipments?.length > 0

// The page cache retries only failed fetches. A successful response with no
// recent shipments or transporters is valid marketplace data for a sparse area.
export const isIncompleteMarketplaceData = (data: {
  upstreamFailed?: boolean
}): boolean => data?.upstreamFailed === true

// Any empty, failed, or timed-out upstream response starts a cooldown. Until it
// passes, callers get cached or empty data without contacting Daedalus.
const getActiveCooldown = (cacheKey: string) => {
  const cooldown = COOLDOWNS[cacheKey]

  return cooldown && Date.now() - cooldown.timestamp < RATE_LIMIT_DURATION
    ? cooldown
    : undefined
}

const freshUpstream = <T>(
  cache: Record<string, TimedEntry<T>>,
  key: string,
): T | undefined => {
  const entry = cache[key]

  if (!entry) {
    return undefined
  }

  const expired = Date.now() - entry.storedAt >= UPSTREAM_CACHE_DURATION

  if (expired) {
    return undefined
  }

  return entry.data
}

const fallbackUpstream = <T>(
  cache: Record<string, TimedEntry<T>>,
  key: string,
): T | undefined => cache[key]?.data

const rememberUpstream = <T>(
  cache: Record<string, TimedEntry<T>>,
  key: string,
  data: T,
) => {
  cache[key] = { data, storedAt: Date.now() }
}

const emptyShipments = (): ShipmentLists => ({
  exportShipments: [],
  importShipments: [],
})

export const getCityShipmentResult = async (
  city: string,
  stateAbbrev: string,
): Promise<FetchResult<ShipmentLists>> => {
  const cacheKey = `${stateAbbrev}:${city}`.toLowerCase()
  const fresh = freshUpstream(CITY_DATA_CACHE, cacheKey)

  if (fresh && hasCompleteShipments(fresh)) {
    return { data: fresh, failed: false }
  }

  const cooldown = getActiveCooldown(cacheKey)

  if (cooldown) {
    if (cooldown.failed) {
      return {
        data: fallbackUpstream(CITY_DATA_CACHE, cacheKey) || emptyShipments(),
        failed: true,
      }
    }

    return {
      data: cooldown.data as ShipmentLists,
      failed: false,
    }
  }

  return shareInFlight(`city-shipments:${cacheKey}`, async () => {
    try {
      const [exportData, importData] = await Promise.all([
        withRetry(() => ShipmentService.getCityExportData(city, stateAbbrev)),
        withRetry(() => ShipmentService.getCityImportData(city, stateAbbrev)),
      ])

      const data = {
        exportShipments: formatShipmentData(exportData?.data?.data),
        importShipments: formatShipmentData(importData?.data?.data),
      }

      if (data.exportShipments?.length && data.importShipments?.length) {
        rememberUpstream(CITY_DATA_CACHE, cacheKey, data)
        delete COOLDOWNS[cacheKey]
      } else {
        COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: false, data }
      }

      return { data, failed: false }
    } catch (error) {
      console.error(`Failed to fetch data for ${cacheKey}:`, error)
      COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: true }

      return {
        data: fallbackUpstream(CITY_DATA_CACHE, cacheKey) || emptyShipments(),
        failed: true,
      }
    }
  })
}

export const getCityShipmentData = async (
  city: string,
  stateAbbrev: string,
): Promise<ShipmentLists> =>
  (await getCityShipmentResult(city, stateAbbrev)).data

export const getStateShipmentResult = async (
  state: string,
): Promise<FetchResult<ShipmentLists>> => {
  const cacheKey = state.toLowerCase()
  const fresh = freshUpstream(STATE_DATA_CACHE, cacheKey)

  if (fresh && hasCompleteShipments(fresh)) {
    return { data: fresh, failed: false }
  }

  const cooldown = getActiveCooldown(cacheKey)

  if (cooldown) {
    if (cooldown.failed) {
      return {
        data: fallbackUpstream(STATE_DATA_CACHE, cacheKey) || emptyShipments(),
        failed: true,
      }
    }

    return {
      data: cooldown.data as ShipmentLists,
      failed: false,
    }
  }

  return shareInFlight(`state-shipments:${cacheKey}`, async () => {
    try {
      const [exportData, importData] = await Promise.all([
        withRetry(() => ShipmentService.getStateExportData(state)),
        withRetry(() => ShipmentService.getStateImportData(state)),
      ])

      const data = {
        exportShipments: formatShipmentData(exportData?.data?.data),
        importShipments: formatShipmentData(importData?.data?.data),
      }

      if (data.exportShipments?.length && data.importShipments?.length) {
        rememberUpstream(STATE_DATA_CACHE, cacheKey, data)
        delete COOLDOWNS[cacheKey]
      } else {
        COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: false, data }
      }

      return { data, failed: false }
    } catch (error) {
      console.error("Failed to fetch state shipment data:", { state, error })
      COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: true }

      return {
        data: fallbackUpstream(STATE_DATA_CACHE, cacheKey) || emptyShipments(),
        failed: true,
      }
    }
  })
}

export const getStateShipmentData = async (
  state: string,
): Promise<ShipmentLists> => (await getStateShipmentResult(state)).data

export type TransporterRow = {
  userId: number
  pickupCity: string
  pickupStateCode: string
  dba: string
  vehicleType: string
  avgRating: string
  photo: string | null
}

export type AnnotatedTransporter = TransporterRow & {
  hasActiveProfile: boolean
}

interface ShipmentApiRow {
  id: string
  legacyId: number
  pickupCity: string
  pickupZipCode: string
  pickupStateCode: string
  deliveryCity: string
  deliveryZipCode: string
  deliveryStateCode: string
  petBreed: string
  petName: string
  petsize: string
  subCategory: string
  distance: string
  price: string
  driverName: string
  rating: string
  profilePhotoUrl: string
  image: string | null
}

export interface ShipmentRow extends ShipmentApiRow {
  breed: string
  animalName: string
  tags: [string, number]
  customer: { image: null; name: string; rating: string }
  subType: string
  deliverCity: string
  deliverStateCode: string
  photo: string
}

export interface ShipmentData {
  exportShipments: ShipmentRow[]
  importShipments: ShipmentRow[]
}

export const getTransportersResult = async (
  type: "state" | "city",
  city: string,
  stateAbbrev?: string,
): Promise<FetchResult<TransporterRow[]>> => {
  const cacheKey =
    type === "state"
      ? `state:${city}`.toLowerCase()
      : `city:${city}:${stateAbbrev}`.toLowerCase()

  const fresh = freshUpstream(TRANSPORTER_CACHE, cacheKey)

  if (fresh && fresh.length > 0) {
    return { data: fresh, failed: false }
  }

  const cooldown = getActiveCooldown(cacheKey)

  if (cooldown) {
    if (cooldown.failed) {
      return {
        data: fallbackUpstream(TRANSPORTER_CACHE, cacheKey) || [],
        failed: true,
      }
    }

    return {
      data: cooldown.data as TransporterRow[],
      failed: false,
    }
  }

  return shareInFlight(`transporters:${cacheKey}`, async () => {
    try {
      const response =
        type === "state"
          ? await withRetry(() => ShipmentService.getStateTransporters(city))
          : await withRetry(() =>
              ShipmentService.getCityTransporters(city, stateAbbrev),
            )

      const transporters = response?.data?.data || []
      const data: TransporterRow[] = transporters
        .filter(Boolean)
        .map((t: any) => ({
          userId: t.userId || 0,
          pickupCity: t.pickupCity || "",
          pickupStateCode: t.pickupStateCode || "",
          dba: t.dba || "",
          vehicleType: t.vehicleType || "",
          avgRating: t.avgRating || "0",
          photo: t.photo || null,
        }))

      // Cache raw rows only — annotation happens in GSSP so validateDriver stays
      // out of the pet-LLP client bundle.
      if (data.length > 0) {
        rememberUpstream(TRANSPORTER_CACHE, cacheKey, data)
        delete COOLDOWNS[cacheKey]
      } else {
        // Avoid immediately re-polling an empty market on another caller.
        COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: false, data }
      }

      return { data, failed: false }
    } catch (error: unknown) {
      console.error("Failed to fetch transporters data:", {
        type,
        city,
        error,
      })
      COOLDOWNS[cacheKey] = { timestamp: Date.now(), failed: true }

      return {
        data: fallbackUpstream(TRANSPORTER_CACHE, cacheKey) || [],
        failed: true,
      }
    }
  })
}

export const getTransportersData = async (
  type: "state" | "city",
  city: string,
  stateAbbrev?: string,
): Promise<TransporterRow[]> =>
  (await getTransportersResult(type, city, stateAbbrev)).data

export const formatShipmentData = (
  data: ShipmentApiRow[] | null = [],
): ShipmentRow[] => {
  return (data ?? []).map((item) => ({
    ...item,
    breed: item.petBreed,
    animalName: item.petName,
    pickupStateCode: item.pickupStateCode,
    pickupZipCode: item.pickupZipCode,
    tags: [item.petsize, parseFloat(item.distance)],
    customer: {
      image: null,
      name: item.driverName,
      rating: item.rating,
    },
    subType: item.subCategory,
    deliverCity: item.deliveryCity,
    deliverStateCode: item.deliveryStateCode,
    photo: item.profilePhotoUrl,
  }))
}

export default cacheStore
